import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  XCircle,
  CheckCircle2,
  Clock,
  Zap,
  Timer,
  AlertTriangle,
  QrCode,
} from "lucide-react";

export const KyuStoreSplitChallenge: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Color Tokens
  const redBg = "#1C0A0A";
  const redBorder = "#EF4444";
  const greenBg = "#061A12";
  const greenBorder = "#10B981";
  const paperText = "#F8FAFC";

  // Stopwatch calculation (Simulated milliseconds)
  const msCount = Math.min(82, Math.floor((frame / 30) * 82));
  const msDisplay = msCount < 10 ? `0${msCount}` : `${msCount}`;

  // Subtitles Safe Zone
  const SUBTITLES = [
    { from: 0, to: 120, text: "Bandingin sendiri: beli manual lewat chat vs di web KyuStore!", highlight: "Bandingin sendiri" },
    { from: 120, to: 270, text: "Di toko lain: nunggu admin berjam-jam, salah ketik ID resiko hangus.", highlight: "resiko hangus" },
    { from: 270, to: 480, text: "Di KyuStore: sistem otomatis cek nickname, anti salah transfer!", highlight: "anti salah transfer" },
    { from: 480, to: 690, text: "Scan QRIS, nol koma delapan detik saldo langsung mendarat!", highlight: "0.8 detik" },
    { from: 690, to: 935, text: "Tinggalkan cara lama. Pindah ke kyustore.com sekarang!", highlight: "kyustore.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#07080B",
        color: paperText,
        fontFamily: "'Rajdhani', -apple-system, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. Header Punch (Y: 240 - 320 px) */}
      <div
        style={{
          position: "absolute",
          top: 250,
          left: 40,
          right: 40,
          textAlign: "center",
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "6px 20px",
            borderRadius: 999,
            background: "rgba(255, 255, 255, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          <Timer size={20} color="#F59E0B" />
          <span>REAL-TIME SPEED TEST</span>
        </div>
        <h1
          style={{
            marginTop: 12,
            fontSize: 52,
            fontWeight: 900,
            lineHeight: 1.05,
            textTransform: "uppercase",
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: "0.02em",
          }}
        >
          CARA LAMA <span style={{ color: "#EF4444" }}>VS</span> WEB BARU
        </h1>
      </div>

      {/* 2. Split Stage 50:50 Vertical (Y: 410 - 1420 px) */}
      <div
        style={{
          position: "absolute",
          top: 410,
          left: 50,
          right: 50,
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}
      >
        {/* UPPER STAGE: Cara Kuno (The Friction Side) */}
        <div
          style={{
            height: 440,
            backgroundColor: redBg,
            borderRadius: 28,
            border: `2px solid ${redBorder}`,
            padding: "24px 28px",
            boxShadow: "0 15px 35px rgba(239, 68, 68, 0.2)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Label Tag */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span
              style={{
                fontSize: 16,
                fontWeight: 900,
                color: "#FFFFFF",
                backgroundColor: redBorder,
                padding: "4px 14px",
                borderRadius: 8,
                letterSpacing: "0.05em",
              }}
            >
              CARA KUNO (CHAT MANUAL)
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#EF4444" }}>
              <Clock size={20} />
              <span style={{ fontSize: 24, fontWeight: 900, fontFamily: "monospace" }}>45:00 MIN</span>
            </div>
          </div>

          {/* Chat Drama Simulation */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* User message */}
            <div
              style={{
                alignSelf: "flex-end",
                backgroundColor: "#2E1515",
                padding: "10px 18px",
                borderRadius: "16px 16px 4px 16px",
                fontSize: 22,
                fontWeight: 600,
                maxWidth: "80%",
              }}
            >
              Min, mau top up diamond dong...
            </div>
            {/* Bot/Admin delay */}
            <div
              style={{
                alignSelf: "flex-start",
                backgroundColor: "#1F1212",
                border: "1px solid rgba(239, 68, 68, 0.4)",
                padding: "10px 18px",
                borderRadius: "16px 16px 16px 4px",
                fontSize: 20,
                color: "#FCA5A5",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <AlertTriangle size={18} color="#EF4444" />
              <span>Admin offline • Dibalas 3 jam kemudian</span>
            </div>
          </div>

          {/* Pain Point Summary */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderTop: "1px solid rgba(239, 68, 68, 0.3)",
              paddingTop: 12,
              fontSize: 18,
              fontWeight: 700,
              color: "#FCA5A5",
            }}
          >
            <span>❌ Nanya Rekening Manual</span>
            <span>❌ Salah ID Resiko Hangus</span>
          </div>
        </div>

        {/* LOWER STAGE: Cara Baru KyuStore (The Instant Speed Side) */}
        <div
          style={{
            height: 480,
            backgroundColor: greenBg,
            borderRadius: 28,
            border: `2.5px solid ${greenBorder}`,
            padding: "24px 28px",
            boxShadow: "0 20px 45px rgba(16, 185, 129, 0.25)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Label Tag */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span
              style={{
                fontSize: 16,
                fontWeight: 900,
                color: "#000000",
                backgroundColor: greenBorder,
                padding: "4px 14px",
                borderRadius: 8,
                letterSpacing: "0.05em",
              }}
            >
              KYUSTORE.COM (WEB OTOMATIS)
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#10B981" }}>
              <Zap size={22} />
              <span style={{ fontSize: 32, fontWeight: 900, fontFamily: "monospace", color: "#34D399" }}>
                00:00.{msDisplay}s
              </span>
            </div>
          </div>

          {/* Live Action UI Display */}
          <div
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              borderRadius: 20,
              padding: "16px 20px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* Auto Cek ID */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: "#94A3B8" }}>Ketik ID 88492019</span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#34D399",
                  background: "rgba(16, 185, 129, 0.15)",
                  padding: "4px 10px",
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 800,
                }}
              >
                <CheckCircle2 size={16} />
                <span>ZURA (VERIFIED)</span>
              </div>
            </div>

            {/* QRIS Settlement */}
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: 12,
                  background: "rgba(16, 185, 129, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <QrCode size={30} color="#34D399" />
              </div>
              <div>
                <div style={{ fontSize: 24, fontWeight: 900, color: "#FFFFFF" }}>
                  SCAN QRIS SAT-SET
                </div>
                <div style={{ fontSize: 16, color: "#34D399", fontWeight: 700 }}>
                  0.8 Detik Saldo Langsung Masuk ke Akun
                </div>
              </div>
            </div>
          </div>

          {/* Benefit Summary */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderTop: "1px solid rgba(16, 185, 129, 0.3)",
              paddingTop: 12,
              fontSize: 18,
              fontWeight: 800,
              color: "#34D399",
            }}
          >
            <span>✓ Cek Nickname Otomatis</span>
            <span>✓ 100% Tanpa Password</span>
          </div>
        </div>
      </div>

      {/* 3. Subtitles Safe Zone (Bottom 390px) */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 390,
            left: 60,
            right: 60,
            display: "flex",
            justifyContent: "center",
            zIndex: 20,
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(7, 8, 11, 0.95)",
              border: "1.5px solid rgba(255, 255, 255, 0.2)",
              borderRadius: 16,
              padding: "16px 28px",
              boxShadow: "0 12px 35px rgba(0, 0, 0, 0.8)",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 30, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.25 }}>
              {currentSub.text.split(currentSub.highlight).map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span
                      style={{
                        color: "#34D399",
                        borderBottom: "2px solid #34D399",
                        paddingBottom: 2,
                      }}
                    >
                      {currentSub.highlight}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>
      )}

      {/* 4. Audio */}
      <Audio
        src={staticFile("kyustore_web_promo_enceladus.mp3")}
        volume={1.0}
      />
    </AbsoluteFill>
  );
};
